// src/views/Users/Customers/AddProjectForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { Button } from "react-bootstrap";
import { ToastifyService } from "../../services";
import { projectActions, recruiterActions } from "../../redux/actions";

const DEFAULT_VALUES = {
  startDate: "",
  endDate: "",
  recruiters: [],
  projects: [],
};

export default function RecruiterReports() {
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState(null); // "recruiters" or "projects"

  const recruiterAllData =
    useSelector((s) => s.recruiterReducer.allData) || [];
  
  const projectsAllData =
    useSelector((s) => s.projectReducer.allData) || [];

  const {
    handleSubmit,
    control,
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: "onChange",
  });

  const watchRecruiters = watch("recruiters");
  const watchProjects = watch("projects");

  // ----------------------------------
  // Recruiter Options + Select All
  // ----------------------------------
  const recruiterOptions = useMemo(() => {
    const options = recruiterAllData.map((r) => ({
      value: r.id,
      label: r.name,
    }));

    return [
      { value: "ALL", label: "All Recruiters" },
      ...options,
    ];
  }, [recruiterAllData]);

  // Get all recruiter options without ALL
  const recruiterOptionsWithoutAll = useMemo(() => {
    return recruiterAllData.map((r) => ({
      value: r.id,
      label: r.name,
    }));
  }, [recruiterAllData]);

  // ----------------------------------
  // Project Options with Grouping
  // ----------------------------------
  const projectOptions = useMemo(() => {
    const activeProjects = [];
    const inactiveProjects = [];
    
    projectsAllData.forEach((project) => {
      const option = {
        value: project.id,
        label: project.positionName,
        isActive: project.isActive,
      };
      
      if (project.isActive) {
        activeProjects.push(option);
      } else {
        inactiveProjects.push(option);
      }
    });

    const groups = [];
    
    if (activeProjects.length > 0) {
      groups.push({
        label: "Active Projects",
        options: activeProjects,
      });
    }
    
    if (inactiveProjects.length > 0) {
      groups.push({
        label: "Inactive Projects",
        options: inactiveProjects,
      });
    }
    
    // Add "Select All" option for both groups
    if (groups.length > 0) {
      return [
        { value: "ALL", label: "All Projects", isGroupHeader: true },
        ...groups,
      ];
    }
    
    return [];
  }, [projectsAllData]);

  // Get all project options without ALL
  const allProjectOptions = useMemo(() => {
    const options = [];
    projectOptions.forEach(group => {
      if (group.options) {
        options.push(...group.options);
      }
    });
    return options;
  }, [projectOptions]);

  // ----------------------------------
  // Select All Projects Handler - UPDATED
  // ----------------------------------
  const handleSelectAllProjects = (selected, action) => {
    // When user clicks the "X" to remove a selected item
    if (action.action === 'remove-value') {
      const removedValue = action.removedValue;
      
      // If "ALL" is being removed, clear everything
      if (removedValue.value === "ALL") {
        return [];
      }
      
      // If a regular item is being removed, filter it out
      const newSelected = selected.filter(opt => opt.value !== removedValue.value);
      
      // Check if we still have all items selected (excluding the removed one)
      const hasAllItems = newSelected.length === allProjectOptions.length;
      if (hasAllItems) {
        return [{ value: "ALL", label: "All Projects" }];
      }
      
      return newSelected;
    }
    
    // When user selects from dropdown
    if (action.action === 'select-option') {
      const selectedOption = action.option;
      
      // If "ALL" is selected
      if (selectedOption.value === "ALL") {
        // Check if "ALL" is already in the selection (means we're adding it)
        const hasAll = selected?.some(opt => opt.value === "ALL");
        if (!hasAll) {
          // Select only "ALL", clear any other selections
          return [{ value: "ALL", label: "All Projects" }];
        }
      }
      
      // If selecting a regular option
      let newSelected = [...(selected || [])];
      
      // If "ALL" is currently selected, remove it when selecting individual items
      newSelected = newSelected.filter(opt => opt.value !== "ALL");
      
      // Add the new selection if not already present
      if (!newSelected.some(opt => opt.value === selectedOption.value)) {
        newSelected.push(selectedOption);
      }
      
      // Check if all items are now selected
      if (newSelected.length === allProjectOptions.length) {
        return [{ value: "ALL", label: "All Projects" }];
      }
      
      return newSelected;
    }
    
    // When user clears the entire selection
    if (action.action === 'clear') {
      return [];
    }
    
    return selected;
  };

  // ----------------------------------
  // Select All Recruiters Handler - UPDATED
  // ----------------------------------
  const handleSelectAllRecruiters = (selected, action) => {
    // When user clicks the "X" to remove a selected item
    if (action.action === 'remove-value') {
      const removedValue = action.removedValue;
      
      // If "ALL" is being removed, clear everything
      if (removedValue.value === "ALL") {
        return [];
      }
      
      // If a regular item is being removed, filter it out
      const newSelected = selected.filter(opt => opt.value !== removedValue.value);
      
      // Check if we still have all items selected (excluding the removed one)
      const hasAllItems = newSelected.length === recruiterOptionsWithoutAll.length;
      if (hasAllItems) {
        return [{ value: "ALL", label: "All Recruiters" }];
      }
      
      return newSelected;
    }
    
    // When user selects from dropdown
    if (action.action === 'select-option') {
      const selectedOption = action.option;
      
      // If "ALL" is selected
      if (selectedOption.value === "ALL") {
        // Check if "ALL" is already in the selection (means we're adding it)
        const hasAll = selected?.some(opt => opt.value === "ALL");
        if (!hasAll) {
          // Select only "ALL", clear any other selections
          return [{ value: "ALL", label: "All Recruiters" }];
        }
      }
      
      // If selecting a regular option
      let newSelected = [...(selected || [])];
      
      // If "ALL" is currently selected, remove it when selecting individual items
      newSelected = newSelected.filter(opt => opt.value !== "ALL");
      
      // Add the new selection if not already present
      if (!newSelected.some(opt => opt.value === selectedOption.value)) {
        newSelected.push(selectedOption);
      }
      
      // Check if all items are now selected
      if (newSelected.length === recruiterOptionsWithoutAll.length) {
        return [{ value: "ALL", label: "All Recruiters" }];
      }
      
      return newSelected;
    }
    
    // When user clears the entire selection
    if (action.action === 'clear') {
      return [];
    }
    
    return selected;
  };

  // Get display value for projects
  const getProjectDisplayValue = (value) => {
    if (!value || value.length === 0) return [];
    
    return value;
  };

  // Get display value for recruiters
  const getRecruiterDisplayValue = (value) => {
    if (!value || value.length === 0) return [];
    
    return value;
  };

  // Custom value renderer to handle "All" display
  const formatOptionLabel = ({ value, label }, { context }) => {
    if (context === 'value' && value === "ALL") {
      return label; // Just show "All Recruiters" or "All Projects"
    }
    return label;
  };

  // ----------------------------------
  // Set Default Values on Load
  // ----------------------------------
  useEffect(() => {
    // Set default selected option to recruiters
    setSelectedOption("recruiters");
    
    // Select all recruiters by default - show as "ALL"
    setValue("recruiters", [{ value: "ALL", label: "All Recruiters" }]);
    
    // Select all projects by default - show as "ALL"
    setValue("projects", [{ value: "ALL", label: "All Projects" }]);
    
    // eslint-disable-next-line
  }, [recruiterOptions.length, projectOptions.length]);

  // ----------------------------------
  // onSubmit Handler - UPDATED
  // ----------------------------------
  const onSubmit = (values) => {
    const { startDate, endDate, recruiters, projects } = values;

    // -----------------------------
    // Only one validation: recruiter OR project must be selected
    // -----------------------------
    const hasRecruiters = recruiters && recruiters.length > 0;
    const hasProjects = projects && projects.length > 0;
    
    if (!hasRecruiters && !hasProjects) {
      ToastifyService.showError("Please select either recruiters or projects (or both)");
      return;
    }

    // -----------------------------
    // Convert dates to epoch seconds
    // -----------------------------
    const startEpoch = Math.floor(
      new Date(`${startDate}T00:00:00Z`).getTime() / 1000
    );

    const endEpoch = Math.floor(
      new Date(`${endDate}T23:59:59Z`).getTime() / 1000
    );

    // -----------------------------
    // Prepare Recruiter IDs
    // -----------------------------
    let recruiterIds = [];
    if (hasRecruiters) {
      // Check if "ALL" is selected
      const hasAllRecruiter = recruiters.some(r => r.value === "ALL");
      if (hasAllRecruiter) {
        // Include all recruiters
        recruiterIds = recruiterOptionsWithoutAll.map(r => Number(r.value));
      } else {
        // Include only selected recruiters
        recruiterIds = recruiters.map(r => Number(r.value));
      }
    }

    // -----------------------------
    // Prepare Project IDs
    // -----------------------------
    let projectIds = [];
    if (hasProjects) {
      // Check if "ALL" is selected
      const hasAllProjects = projects.some(p => p.value === "ALL");
      if (hasAllProjects) {
        // Include all projects
        projectIds = allProjectOptions.map(p => Number(p.value));
      } else {
        // Include only selected projects
        projectIds = projects.map(p => Number(p.value));
      }
    }

    // -----------------------------
    // Payload matches backend DTO
    // -----------------------------
    const payload = {
      StartDate: startEpoch,
      EndDate: endEpoch,
      RecruiterIds: recruiterIds,
      ProjectIds: projectIds,
    };
    // Dispatch appropriate action based on selection
      dispatch(recruiterActions.report(payload));
  };

  const getAll = () => {
    dispatch(recruiterActions.getAllRecruiterList());
    dispatch(projectActions.getAllProjectListWithIsActive());
  };

  useEffect(() => {
    getAll();
    // eslint-disable-next-line
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="form-design">
      <div className="row g-3">
        {/* Start Date */}
        <div className="col-12 col-md-6">
          <div className="form-floating">
            <input
              type="date"
              className="form-control"
              id="startDate"
              {...register("startDate")}
            />
            <label htmlFor="startDate">Start Date</label>
          </div>
        </div>

        {/* End Date */}
        <div className="col-12 col-md-6">
          <div className="form-floating">
            <input
              type="date"
              className="form-control"
              id="endDate"
              {...register("endDate")}
            />
            <label htmlFor="endDate">End Date</label>
          </div>
        </div>

        {/* Recruiters Multi Select with Select All */}
        <div className="col-12 col-md-6">
          <Controller
            name="recruiters"
            control={control}
            render={({ field }) => (
              <>
                <label className="form-label fw-semibold">
                  Recruiters
                </label>
                <Select
                  {...field}
                  options={recruiterOptions}
                  isMulti
                  closeMenuOnSelect={false}
                  placeholder="Select Recruiters"
                  classNamePrefix="react-select"
                  onChange={(selected, actionMeta) => {
                    const newSelected = handleSelectAllRecruiters(selected, actionMeta);
                    field.onChange(newSelected);
                  }}
                  value={getRecruiterDisplayValue(field.value)}
                  formatOptionLabel={formatOptionLabel}
                  isSearchable
                  hideSelectedOptions={false}
                />
              </>
            )}
          />
        </div>

        {/* Projects Multi Select with Grouping */}
        <div className="col-12 col-md-6">
          <Controller
            name="projects"
            control={control}
            render={({ field }) => (
              <>
                <label className="form-label fw-semibold">
                  Projects
                </label>
                <Select
                  {...field}
                  options={projectOptions}
                  isMulti
                  closeMenuOnSelect={false}
                  placeholder="Select Projects"
                  classNamePrefix="react-select"
                  onChange={(selected, actionMeta) => {
                    const newSelected = handleSelectAllProjects(selected, actionMeta);
                    field.onChange(newSelected);
                  }}
                  value={getProjectDisplayValue(field.value)}
                  formatOptionLabel={formatOptionLabel}
                  isSearchable
                  hideSelectedOptions={false}
                  formatGroupLabel={(group) => (
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{group.label}</span>
                      <span className="badge bg-secondary">
                        {group.options.length}
                      </span>
                    </div>
                  )}
                />
              </>
            )}
          />
        </div>

        {/* Validation Message */}
        <div className="col-12">
          {!watchRecruiters?.length && !watchProjects?.length && (
            <small className="text-danger">
              Please select at least one recruiter or one project
            </small>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4 form-footer">
        <Button type="submit" className="btn-footer" variant="success">
          Save
        </Button>
      </div>
    </form>
  );
}